import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "../lib/api";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Package, Power } from "@phosphor-icons/react";
import { toast } from "sonner";

const UNITA = ["forfait", "ora", "giornata", "persona", "anno", "mese", "pezzo"];

const EMPTY = { codice: "", nome: "", descrizione: "", prezzo: 0, unita: "forfait", categoria: "", attivo: true, consenti_abbonamento: false, abbonamento_rate_count: 12 };

export default function Catalogo() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterAttivo, setFilterAttivo] = useState("attivi");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/catalogo");
      setItems(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function openNew() { setForm(EMPTY); setEditing("new"); }
  function openEdit(it) { setForm({ ...EMPTY, ...it }); setEditing(it); }
  function close() { setEditing(null); }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, prezzo: Number(form.prezzo) };
      if (editing === "new") {
        await api.post("/catalogo", payload);
        toast.success("Servizio creato");
      } else {
        await api.put(`/catalogo/${editing.id}`, payload);
        toast.success("Servizio aggiornato");
      }
      close();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  }

  async function toggleActive(it) {
    try {
      await api.put(`/catalogo/${it.id}`, { ...it, attivo: !it.attivo });
      await load();
    } catch (err) { toast.error("Errore"); }
  }

  async function remove(it) {
    if (!window.confirm(`Eliminare "${it.nome}"?`)) return;
    try {
      await api.delete(`/catalogo/${it.id}`);
      toast.success("Eliminato");
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore"); }
  }

  const filtered = items.filter((it) => {
    const text = [it.codice, it.nome, it.categoria, it.descrizione].filter(Boolean).join(" ").toLowerCase();
    const matchSearch = text.includes(search.toLowerCase());
    const matchAttivo = filterAttivo === "tutti" || (filterAttivo === "attivi" ? it.attivo : !it.attivo);
    return matchSearch && matchAttivo;
  });

  const categorieUniche = Array.from(new Set(items.map(i => i.categoria).filter(Boolean)));

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Listino</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Catalogo Servizi</h1>
          <p className="text-sm text-[#64748B] mt-3">{items.length} servizi · {categorieUniche.length} categorie</p>
        </div>
        <button onClick={openNew} data-testid="new-servizio-btn" className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start">
          <Plus size={14} weight="bold" /> Nuovo Servizio
        </button>
      </div>

      <div className="swiss-card">
        <div className="p-4 border-b border-[#E2E8F0] flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
            <input placeholder="Cerca codice, nome, categoria..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="catalogo-search" className="w-full bg-white border border-[#E2E8F0] rounded-sm pl-10 pr-3 py-2 text-sm focus:border-[#0033A0] outline-none" />
          </div>
          <div className="flex gap-1.5">
            {[
              { v: "attivi", label: "Attivi" },
              { v: "disattivi", label: "Disattivi" },
              { v: "tutti", label: "Tutti" },
            ].map(o => (
              <button key={o.v} onClick={() => setFilterAttivo(o.v)} className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded-sm border transition-all ${filterAttivo === o.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#0A0A0A]"}`}>{o.label}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-[#64748B]">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Package size={32} className="mx-auto text-[#64748B] mb-3" />
            <p className="text-sm text-[#64748B]">{items.length === 0 ? "Nessun servizio nel catalogo." : "Nessun risultato."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F5F7]">
                <tr className="text-left">
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Codice</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Nome</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Categoria</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Unità</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold text-right">Prezzo</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Stato</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(it => (
                  <tr key={it.id} className={`border-t border-[#E2E8F0] hover:bg-[#F4F5F7] ${!it.attivo ? "opacity-50" : ""}`} data-testid={`servizio-row-${it.id}`}>
                    <td className="py-3 px-4 font-mono text-xs font-bold">{it.codice}</td>
                    <td className="py-3 px-4">
                      <div className="font-semibold flex items-center gap-2">
                        {it.nome}
                        {it.consenti_abbonamento && (
                          <span className="px-1.5 py-0.5 text-[8px] uppercase tracking-[0.15em] font-bold bg-[#008A3C] text-white rounded-sm" title={`Pagamento rateale ${it.abbonamento_rate_count || 12} mesi`}>RATE</span>
                        )}
                      </div>
                      {it.descrizione && <div className="text-xs text-[#64748B] mt-0.5 max-w-md truncate">{it.descrizione}</div>}
                    </td>
                    <td className="py-3 px-4 text-[#64748B]">{it.categoria || "—"}</td>
                    <td className="py-3 px-4 text-[#64748B] text-xs uppercase tracking-[0.1em]">{it.unita}</td>
                    <td className="py-3 px-4 text-right font-mono font-bold">{formatCurrency(it.prezzo)}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border rounded-sm ${it.attivo ? "bg-green-100 text-green-800 border-green-300" : "bg-gray-100 text-gray-800 border-gray-300"}`}>{it.attivo ? "Attivo" : "Inattivo"}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => toggleActive(it)} className="p-1.5 text-[#64748B] hover:text-[#0033A0]" title="Attiva/Disattiva" data-testid={`toggle-servizio-${it.id}`}><Power size={16} /></button>
                        <button onClick={() => openEdit(it)} className="p-1.5 text-[#64748B] hover:text-[#0033A0]" data-testid={`edit-servizio-${it.id}`}><PencilSimple size={16} /></button>
                        <button onClick={() => remove(it)} className="p-1.5 text-[#64748B] hover:text-[#D32F2F]" data-testid={`delete-servizio-${it.id}`}><Trash size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-[#E2E8F0] rounded-sm max-w-2xl w-full max-h-[90vh] overflow-auto thin-scroll" data-testid="servizio-modal">
            <div className="p-6 border-b border-[#E2E8F0] sticky top-0 bg-white">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">{editing === "new" ? "Nuovo" : "Modifica"}</div>
              <h2 className="font-display text-2xl font-bold">Servizio</h2>
            </div>
            <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Codice *"><input required value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value.toUpperCase() })} className="inp font-mono" data-testid="servizio-codice" /></Field>
              <Field label="Categoria">
                <input list="categorie-list" value={form.categoria || ""} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="inp" />
                <datalist id="categorie-list">
                  {categorieUniche.map(c => <option key={c} value={c} />)}
                </datalist>
              </Field>
              <Field label="Nome *" full><input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="inp" data-testid="servizio-nome" /></Field>
              <Field label="Descrizione" full><textarea rows={3} value={form.descrizione || ""} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="inp" data-testid="servizio-descrizione" /></Field>
              <Field label="Prezzo *"><input type="number" min={0} step="0.01" required value={form.prezzo} onChange={(e) => setForm({ ...form, prezzo: e.target.value })} className="inp font-mono" data-testid="servizio-prezzo" /></Field>
              <Field label="Unità">
                <select value={form.unita} onChange={(e) => setForm({ ...form, unita: e.target.value })} className="inp" data-testid="servizio-unita">
                  {UNITA.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </Field>
              <div className="sm:col-span-2 flex items-center gap-2">
                <input id="attivo" type="checkbox" checked={form.attivo} onChange={(e) => setForm({ ...form, attivo: e.target.checked })} className="accent-[#0033A0]" />
                <label htmlFor="attivo" className="text-sm">Servizio <strong>attivo</strong> (disponibile nei preventivi)</label>
              </div>
              <div className="sm:col-span-2 pt-3 border-t border-[#E2E8F0]">
                <div className="flex items-center gap-2">
                  <input id="cons_abb" type="checkbox" checked={!!form.consenti_abbonamento} onChange={(e) => setForm({ ...form, consenti_abbonamento: e.target.checked })} className="accent-[#008A3C]" data-testid="servizio-consenti-abb" />
                  <label htmlFor="cons_abb" className="text-sm">Servizio con <strong>pagamento rateale mensile</strong> (es. SIS-DIG, software annuali)</label>
                </div>
                {form.consenti_abbonamento && (
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Numero rate mensili">
                      <input type="number" min={2} max={36} value={form.abbonamento_rate_count} onChange={(e) => setForm({ ...form, abbonamento_rate_count: e.target.value })} className="inp font-mono" data-testid="servizio-rate-count" />
                    </Field>
                    <div className="text-xs bg-[#008A3C]/5 rounded-sm p-3 leading-relaxed">
                      Quando aggiungi questo servizio in un preventivo, l'opzione rateale si attiva <strong>automaticamente</strong>.
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 border-t border-[#E2E8F0] flex justify-end gap-3 sticky bottom-0 bg-white">
              <button type="button" onClick={close} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]">Annulla</button>
              <button type="submit" disabled={saving} className="btn-primary px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm" data-testid="servizio-save-btn">{saving ? "…" : "Salva"}</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp { width: 100%; background: #fff; border: 1px solid #E2E8F0; border-radius: 4px; padding: 8px 12px; font-size: 14px; outline: none; } .inp:focus { border-color: #0033A0; box-shadow: 0 0 0 1px #0033A0; }`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
